/**
 * Cancels in-flight list fetches when filters change so only the latest response is applied.
 * Use one instance per list (e.g. createCancellableListFetch() in a module or hook).
 */
export interface CancellableListFetchHandle {
  signal?: AbortSignal;
  generation: number;
}

export function createCancellableListFetch() {
  let abortController: AbortController | null = null;
  let generation = 0;

  return {
    begin(isFirstPage: boolean): CancellableListFetchHandle {
      if (!isFirstPage) {
        return { generation };
      }

      abortController?.abort();
      abortController = new AbortController();
      generation += 1;

      return {
        signal: abortController.signal,
        generation,
      };
    },
    isStale(requestGeneration: number): boolean {
      return requestGeneration !== generation;
    },
    invalidate(): void {
      abortController?.abort();
      abortController = null;
      generation += 1;
    },
  };
}

export function isAbortLikeMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('user aborted')
    || normalized.includes('signal is aborted')
    || normalized === 'the operation was aborted.'
    || normalized === 'fetch is aborted'
    || normalized.includes('request signal is aborted')
  );
}

export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error) {
    if (error.name === 'AbortError') return true;
    if (isAbortLikeMessage(error.message)) return true;
  }

  return false;
}
